import React, { useState, useRef, useEffect } from "react";
import "./RoomSelector.css";
import { MenuItem, Select, FormControl, InputLabel, Box } from "@mui/material";
import minus from "../../assets/images/packages/svg/minus.svg";
import plus from "../../assets/images/packages/svg/plus.svg";
import bedIcon from "../../assets/images/packages/svg/fluent_bed-24-regular.svg";
import bedIconDisable from "../../assets/images/packages/svg/fluent_bed-24.svg";
import { useTranslation } from "../../contexts/TranslationContext";

const RoomSelector = ({ totalTravelers,single, double, triple, setSingle, setDouble, setTriple,onRoomsChange  }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const handleOpen = () => setOpen(!open);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getTotalOccupancy = (s, d, t) => s + d * 2 + t * 3;

  const handleChange = (setter, value) => {
    const newTotal = getTotalOccupancy(
      setter === setSingle ? value : single,
      setter === setDouble ? value : double,
      setter === setTriple ? value : triple
    );

    if (value >= 0 && newTotal <= totalTravelers) {
      setter(value);
    }
  };

  const canIncrease = (s, d, t, roomType) => {
  const total = s + d * 2 + t * 3; 
  if (roomType === "single") return total + 1 <= totalTravelers;
  if (roomType === "double") return total + 2 <= totalTravelers;
  if (roomType === "triple") return total + 3 <= totalTravelers;
  return false;
};

  useEffect(() => {
    setSingle(0);
    setDouble(0);
    setTriple(0);
  }, [totalTravelers]);

  return (
    <div className="traveler-container" ref={containerRef}>
      <div className="traveler-input" onClick={handleOpen}>
        {t("NumberOfRooms")} : {single + double + triple}
      </div>

      {open && (
        <div className="traveler-dropdown room-selector-box">
          <h4 className="room-title">{t("NumberOfRooms")}</h4>
          <p className="room-subtitle">{t("RoomSelectorText")}</p>

          {/* Single Room */}
          <div
            className="traveler-counter-container room-counter-container"
            style={{
              opacity: canIncrease(single, double, triple, "single") ? 1 : 0.4,
            }}
          >
            <div className="room-label">
              {[...Array(1)].map((_, i) => (
                <img
                  key={i}
                  src={
                    canIncrease(single, double, triple, "single")
                      ? bedIcon
                      : bedIconDisable
                  }
                  alt="bed"
                  className="room-icon"
                />
              ))}
              <span >
                {t("SingleRoom")}
              </span>
            </div>
            <div className="traveler-counter">
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChange(setSingle, single - 1)}
              >
                <img src={minus} alt="minus" />
              </button>
              <span>{single}</span>
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChange(setSingle, single + 1)}
                disabled={!canIncrease(single, double, triple, "single")}
              >
                <img src={plus} alt="plus" />
              </button>
            </div>
          </div>

          {/* Double Room */}
          <div className="traveler-counter-container room-counter-container"
                      style={{
              opacity: canIncrease(single, double, triple, "double") ? 1 : 0.4,
            }}
          >
            <div className="room-label">
              {[...Array(2)].map((_, i) => (
                <span key={i}>
                  <img
                    src={
                      canIncrease(single, double, triple, "double")
                        ? bedIcon
                        : bedIconDisable
                    }
                    alt="bed"
                    className="room-icon"
                  />
                  {i < 2 - 1 && " + "}
                </span>
              ))}
              <span >
                {t("DoubleRoom")}
              </span>
            </div>
            <div className="traveler-counter">
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChange(setDouble, double - 1)}
              >
                <img src={minus} alt="minus" />
              </button>
              <span>{double}</span>
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChange(setDouble, double + 1)}
                disabled={!canIncrease(single, double, triple, "double")}
              >
                <img src={plus} alt="plus" />
              </button>
            </div>
          </div>

          {/* Triple Room */}
          <div className="traveler-counter-container room-counter-container"
                                style={{
              opacity: canIncrease(single, double, triple, "triple") ? 1 : 0.4,
            }}
          >
            <div className="room-label">
              {[...Array(3)].map((_, i) => (
                <span key={i}>
                  <img
                    src={
                      canIncrease(single, double, triple, "triple")
                        ? bedIcon
                        : bedIconDisable
                    }
                    alt="bed"
                    className="room-icon"
                  />
                  {i < 3 - 1 && " + "}
                </span>
              ))}
              <span >
                {t("TripleRoom")}
              </span>
            </div>
            <div className="traveler-counter">
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChange(setTriple, triple - 1)}
              >
                <img src={minus} alt="minus" />
              </button>
              <span>{triple}</span>
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChange(setTriple, triple + 1)}
                disabled={!canIncrease(single, double, triple, "triple")}
              >
                <img src={plus} alt="plus" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="room-actions">
            <button className="cancel-btn" onClick={() => setOpen(false)}>
              {t("Cancel")}
            </button>
            <button
              className="done-btn"
              onClick={() => {
                onRoomsChange({ single, double, triple });
                setOpen(false);
              }}
            >
              {t("Done")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomSelector;
